import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  confirm,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckIcon,
  CopyIcon,
  ExternalLinkIcon,
  LinkIcon,
  PencilIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { PermissionGate } from "@/modules/auth/presentation/ui/PermissionGate";
import {
  getNextAllowedStatuses,
  type OrderChannel,
  type OrderDetailDTO,
  type OrderStatus,
} from "@/modules/orders/domain/contracts";
import { useRecordOrderPaymentRefund } from "@/modules/payments/presentation/api/queries";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { useSocialSalesSourcesActiveForPosQuery } from "@/modules/social-sales-sources/presentation/api/queries";
import { formatDateLocal } from "@/shared/lib/date-time";
import { formatPriceLak } from "@/shared/lib/format-price";
import { AppImage } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useAddOrderAttachment,
  useAddOrderReturnLines,
  useCancelHalPreOrder,
  useCreateHalPreOrder,
  useOrderQuery,
  useRefreshHalTracking,
  useUpdateHalPreOrder,
  useUpdateOrderChannel,
  useUpdateOrderDiscount,
  useUpdateOrderPaymentMethod,
  useUpdateOrderStatus,
  useUpdateOrderTracking,
} from "../api/queries";
import { OrderAttachmentGrid } from "../ui/OrderAttachmentGrid";
import { OrderCustomerInfoCard } from "../ui/OrderCustomerInfoCard";
import { OrderEditItemsDialog } from "../ui/OrderEditItemsDialog";
import { OrderStatusChangeForm } from "../ui/OrderStatusChangeForm";
import { OrderStatusHistoryTimeline } from "../ui/OrderStatusHistoryTimeline";
import { PrintOrderButton } from "../ui/print/PrintOrderButton";
import {
  formatOrderPaymentMethodLabel,
  isRetailWalkInOrder,
  retailPaymentEmptyMessage,
  retailWalkInStatusLabel,
} from "../utils/order-retail-display";
import { formatPrintVariantOptions } from "../utils/print-types";

const STATUS_LABELS: Record<string, string> = {
  pending_payment: "ລໍຖ້າຊຳລະ",
  confirmed: "ຢືນຢັນ",
  packing: "ກຳລັງແພັກ",
  shipped: "ສົ່ງແລ້ວ",
  completed: "ສຳເລັດ",
  return_in_transit: "ສິນຄ້າຕີກັບກຳລັງມາ",
  returned: "ຕີກັບ / ຮັບຄືນຄັງ",
  cancelled: "ຍົກເລີກ",
};

const STATUS_BADGE_LO: Record<string, string> = {
  pending_payment: "ລໍຖ້າຊຳລະ",
  confirmed: "ກຳລັງດຳເນີນການ",
  packing: "ກຳລັງດຳເນີນການ",
  shipped: "ສົ່ງແລ້ວ",
  completed: "ສຳເລັດ",
  return_in_transit: "ຕີກັບກຳລັງມາ",
  returned: "ຕີກັບ",
  cancelled: "ຍົກເລີກ",
};

const ORDER_PAYMENT_METHOD_LABELS: Record<string, string> = {
  cod: "ຊຳລະປາຍທາງ",
  online: "ອອນໄລນ໌",
};

const ORDER_CHANNEL_LABELS: Record<string, string> = {
  web: "ເວັບ",
  retail: "ຫນ້າຮ້ານ",
  social: "Social Media",
  pos: "ຫນ້າຮ້ານ (ເກົ່າ)",
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຖ້າ",
  paid: "ຊຳລະແລ້ວ",
  failed: "ລົ້ມເຫລວ",
  partially_refunded: "ຄືນບາງສ່ວນ",
  refunded: "ຄືນເງິນ",
  cancelled: "ຍົກເລີກ",
};

const PAYMENT_STATUS_BADGE: Record<
  string,
  "default" | "secondary" | "destructive" | "outline" | "success" | "warning"
> = {
  pending: "warning",
  paid: "success",
  failed: "destructive",
  partially_refunded: "warning",
  refunded: "secondary",
  cancelled: "outline",
};

const STATUS_BADGE_VARIANTS: Record<
  string,
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "muted"
  | "accent"
  | "success"
  | "warning"
  | "info"
> = {
  pending_payment: "warning",
  confirmed: "default",
  packing: "default",
  shipped: "info",
  completed: "success",
  return_in_transit: "info",
  returned: "warning",
  cancelled: "destructive",
};

const STEPPER_STEPS = ["ລໍຖ້າຊຳລະ", "ກຳລັງດຳເນີນການ", "ສົ່ງແລ້ວ", "ສຳເລັດ"] as const;

type HalTrackingEventView = {
  date?: string | null;
  place?: string | null;
  message?: string | null;
  delivery_state?: number | null;
  delivery_state_name?: string | null;
};

function getLatestHalTrackingEvent(
  order: OrderDetailDTO | undefined,
): HalTrackingEventView | null {
  const metadata = order?.halShipment?.metadata;
  if (!metadata || typeof metadata !== "object") return null;
  const event = (metadata as Record<string, unknown>).latestTrackingEvent;
  if (!event || typeof event !== "object") return null;
  return event as HalTrackingEventView;
}

function getErrorMessage(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : String(error);
}

function stepIndexForStatus(status: OrderDetailDTO["status"]): number {
  switch (status) {
    case "pending_payment":
      return 0;
    case "confirmed":
    case "packing":
      return 1;
    case "shipped":
      return 2;
    case "completed":
      return 3;
    case "return_in_transit":
    case "returned":
    case "cancelled":
      return -1;
    default:
      return 0;
  }
}

function OrderStatusStepper({ status }: { status: OrderDetailDTO["status"] }) {
  const current = stepIndexForStatus(status);
  if (current < 0) return null;

  return (
    <div className="flex w-full min-w-0 items-stretch">
      {STEPPER_STEPS.map((label, i) => {
        const isDone = i < current;
        const isCurrent = i === current;
        return (
          <div
            key={label}
            className="flex min-w-0 flex-1 items-center"
            style={{ flexBasis: 0 }}
          >
            <div className="flex min-w-0 flex-1 flex-col items-center">
              <div
                className={`flex size-6 shrink-0 items-center justify-center rounded-full border-2 font-medium text-[10px] sm:size-8 sm:text-xs ${
                  isDone
                    ? "border-primary bg-primary text-primary-foreground"
                    : isCurrent
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-muted-foreground/30 bg-muted/50 text-muted-foreground"
                }`}
              >
                {isDone ? <CheckIcon className="size-4" /> : i + 1}
              </div>
              <span
                className={`mt-1 max-w-full truncate px-0.5 text-center font-medium text-[10px] sm:text-xs ${
                  isCurrent ? "text-foreground" : "text-muted-foreground"
                }`}
                title={label}
              >
                {label}
              </span>
            </div>
            {i < STEPPER_STEPS.length - 1 && (
              <div
                className={`mx-0.5 h-0.5 min-w-2 flex-1 sm:mx-1 sm:min-w-4 ${
                  isDone ? "bg-primary" : "bg-muted"
                }`}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function RetailWalkInStatusBanner({
  status,
}: {
  status: OrderDetailDTO["status"];
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
      <Badge variant="secondary" className="font-normal">
        ຫນ້າຮ້ານ
      </Badge>
      <span className="font-medium text-sm">
        {retailWalkInStatusLabel(status)}
      </span>
      <span className="text-muted-foreground text-xs">
        ຮັບສິນຄ້າທີ່ຮ້ານ — ບໍ່ມີຂັ້ນຕອນຂົນສົ່ງ
      </span>
    </div>
  );
}

function PaymentRefundBlock({
  orderId,
  pay,
  orderStatus,
}: {
  orderId: string;
  pay: OrderDetailDTO["payments"][number];
  orderStatus: OrderStatus;
}) {
  const recordRefund = useRecordOrderPaymentRefund(orderId);
  const [amountStr, setAmountStr] = useState("");
  const [note, setNote] = useState("");
  const already = pay.refundSucceededTotal ?? 0;
  const remaining = pay.amount - already;
  const canRefundByOrderStatus = orderStatus === "returned";
  const paymentRefundable =
    remaining > 0 &&
    (pay.status === "paid" || pay.status === "partially_refunded");
  const canFile = canRefundByOrderStatus && paymentRefundable;

  if (paymentRefundable && !canRefundByOrderStatus) {
    return (
      <div className="mt-2 space-y-1 border-border border-t pt-2">
        <p className="text-muted-foreground text-xs">
          ຄືນແລ້ວ: LAK {formatPriceLak(already)} — ເຫຼືອ: LAK{" "}
          {formatPriceLak(remaining)}
        </p>
        <p className="text-amber-800 text-xs dark:text-amber-400">
          ບັນທຶກຄືນເງິນໄດ້ຫລັງສະຖານະອໍເດີເປັນ «ຕີກັບ / ຮັບຄືນຄັງ» ແລ້ວ (ຮັບສິນຄ້າຕີກັບແລ້ວ)
        </p>
      </div>
    );
  }

  if (!canFile) return null;
  return (
    <div className="mt-2 space-y-2 border-border border-t pt-2">
      <p className="text-muted-foreground text-xs">
        ຄືນແລ້ວ: LAK {formatPriceLak(already)} — ເຫຼືອ: LAK{" "}
        {formatPriceLak(remaining)}
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <Label className="text-xs">ຍອດຄືນ (ຫນ່ວຍນ້ອຍສຸດ)</Label>
          <Input
            type="number"
            min={1}
            max={remaining}
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder={String(remaining)}
            className="font-mono"
          />
        </div>
        <Button
          type="button"
          size="sm"
          disabled={recordRefund.isPending}
          onClick={() => {
            const n = Number.parseInt(amountStr, 10);
            if (!Number.isFinite(n) || n < 1 || n > remaining) {
              toast.error("ກອກຍອດຄືນໃຫ້ຖືກຕ້ອງ");
              return;
            }
            void recordRefund.mutateAsync({
              paymentId: pay.id,
              amount: n,
              note: note || undefined,
            });
            setAmountStr("");
            setNote("");
          }}
        >
          {recordRefund.isPending ? "…" : "ບັນທຶກຄືນເງິນ"}
        </Button>
      </div>
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="ຫມາຍເຫດ (ທາງເລືອກ)"
        rows={2}
        className="text-sm"
      />
    </div>
  );
}

export function OrderDetailPage() {
  const { id } = useParams({ from: "/app/orders/$id" });
  const nav = useNavigate({ from: "/app/orders/$id" });
  const { data: order, isLoading } = useOrderQuery(id);
  const paymentSlipAttachments = useMemo(() => {
    if (!order?.attachments?.length) return [];
    return order.attachments.filter((a) => a.kind === "payment_slip");
  }, [order?.attachments]);

  const updateStatus = useUpdateOrderStatus(id);
  const addAttachment = useAddOrderAttachment(id);
  const updateTracking = useUpdateOrderTracking(id);
  const updateDiscount = useUpdateOrderDiscount(id ?? "");
  const updatePaymentMethod = useUpdateOrderPaymentMethod(id ?? "");
  const updateChannel = useUpdateOrderChannel(id ?? "");
  const addReturnLines = useAddOrderReturnLines(id ?? "");
  const createHalPreOrder = useCreateHalPreOrder(id ?? "");
  const updateHalPreOrder = useUpdateHalPreOrder(id ?? "");
  const cancelHalPreOrder = useCancelHalPreOrder(id ?? "");
  const refreshHalTracking = useRefreshHalTracking(id ?? "");
  const [returnItemId, setReturnItemId] = useState("");
  const [returnQty, setReturnQty] = useState(1);
  const [returnReason, setReturnReason] = useState("");

  const [paymentMethodDraft, setPaymentMethodDraft] = useState<
    "cod" | "online"
  >("cod");

  const [channelDraft, setChannelDraft] = useState<OrderChannel>("web");
  const [socialSourceDraft, setSocialSourceDraft] = useState("");

  const [discountInput, setDiscountInput] = useState("0");
  const [editItemsOpen, setEditItemsOpen] = useState(false);

  const [trackingInput, setTrackingInput] = useState("");
  useEffect(() => {
    if (order?.trackingNumber) setTrackingInput(order.trackingNumber);
    else if (order && !order.trackingNumber) setTrackingInput("");
  }, [order?.id, order?.trackingNumber]);

  useEffect(() => {
    if (order) setDiscountInput(String(order.discountTotal ?? 0));
  }, [order?.id, order?.discountTotal]);

  const orderItemsSubtotal = useMemo(
    () => (order?.items ?? []).reduce((s, i) => s + i.total, 0),
    [order?.items],
  );
  const maxDiscountAllowed = orderItemsSubtotal + (order?.shippingFee ?? 0);

  const remainingReturnable = useCallback(
    (it: OrderDetailDTO["items"][number]) => {
      const used =
        (it.quantityReturnStocked ?? 0) + (it.quantityReturnPending ?? 0);
      return Math.max(0, it.quantity - used);
    },
    [],
  );

  const returnLineItemOptions = useMemo(() => {
    if (!order?.items?.length) return [];
    return order.items.filter((it) => {
      const used =
        (it.quantityReturnStocked ?? 0) + (it.quantityReturnPending ?? 0);
      return it.quantity - used > 0;
    });
  }, [order?.id, order?.items]);

  const resolvedReturnItemId = useMemo(() => {
    if (
      returnItemId &&
      returnLineItemOptions.some((c) => c.id === returnItemId)
    ) {
      return returnItemId;
    }
    return returnLineItemOptions[0]?.id ?? "";
  }, [returnItemId, returnLineItemOptions]);

  const selectedReturnItem = useMemo(
    () => order?.items.find((i) => i.id === resolvedReturnItemId),
    [order?.items, resolvedReturnItemId],
  );

  const maxReturnQty = useMemo(() => {
    if (!selectedReturnItem) return 1;
    return Math.max(1, remainingReturnable(selectedReturnItem));
  }, [selectedReturnItem, remainingReturnable]);

  const getOrderItemLabel = useCallback(
    (orderItemId: string) => {
      const it = order?.items.find((i) => i.id === orderItemId);
      if (it) return (it.productName ?? it.id).trim() || it.id;
      return orderItemId;
    },
    [order?.items],
  );

  useEffect(() => {
    if (order?.paymentMethod === "cod" || order?.paymentMethod === "online") {
      setPaymentMethodDraft(order.paymentMethod);
    } else if (order && order.paymentMethod === null) {
      setPaymentMethodDraft("cod");
    }
  }, [order?.id, order?.paymentMethod]);

  useEffect(() => {
    if (!order) return;
    setChannelDraft(order.channel);
    setSocialSourceDraft(order.socialSalesSource?.id ?? "");
  }, [order?.id, order?.channel, order?.socialSalesSource?.id]);

  const hasBlockingPaymentForMethodEdit = useMemo(
    () =>
      (order?.payments ?? []).some(
        (p) => p.status === "paid" || p.status === "partially_refunded",
      ),
    [order?.payments],
  );

  const canEditPendingOrderMeta = Boolean(
    order &&
      order.status === "pending_payment" &&
      !hasBlockingPaymentForMethodEdit,
  );

  const canEditOrderItems = Boolean(
    order &&
      (order.status === "pending_payment" ||
        order.status === "confirmed" ||
        order.status === "packing") &&
      order.returnLines.length === 0,
  );

  const socialSourcesActiveQuery = useSocialSalesSourcesActiveForPosQuery(
    canEditPendingOrderMeta && channelDraft === "social",
  );

  const channelMetaDirty = useMemo(() => {
    if (!order) return false;
    if (channelDraft !== order.channel) return true;
    if (channelDraft === "social") {
      return socialSourceDraft.trim() !== (order.socialSalesSource?.id ?? "");
    }
    return false;
  }, [order, channelDraft, socialSourceDraft]);

  const socialSalesSourcesList = socialSourcesActiveQuery.data ?? [];

  useEffect(() => {
    if (!order?.items?.length) return;
    if (returnLineItemOptions.length === 0) {
      if (returnItemId) setReturnItemId("");
      return;
    }
    if (
      !returnItemId ||
      !returnLineItemOptions.some((c) => c.id === returnItemId)
    ) {
      const firstReturnable = returnLineItemOptions[0];
      if (firstReturnable) setReturnItemId(firstReturnable.id);
    }
  }, [order?.id, order?.items, returnItemId, returnLineItemOptions]);

  useEffect(() => {
    setReturnQty((q) => {
      if (maxReturnQty < 1) return 1;
      return Math.min(Math.max(1, q), maxReturnQty);
    });
  }, [returnItemId, maxReturnQty, order?.id]);

  const nextStatuses = useMemo(
    () => (order ? getNextAllowedStatuses(order.status) : []),
    [order],
  );
  const canEditTracking =
    order && (order.status === "packing" || order.status === "shipped");
  const isHalOrder = order?.shipping?.shippingCarrierName === "HAL Logistics";
  const halPreOrderCreated = Boolean(
    order?.halShipment?.preOrderId || order?.halShipment?.shipmentNumber,
  );
  const canMutatePendingHalPreOrder =
    halPreOrderCreated && order?.halShipment?.shipmentStatusId === 1;
  const latestHalTrackingEvent = getLatestHalTrackingEvent(order);
  const halActionPending =
    createHalPreOrder.isPending ||
    updateHalPreOrder.isPending ||
    cancelHalPreOrder.isPending ||
    refreshHalTracking.isPending;
  const halActionError =
    getErrorMessage(createHalPreOrder.error) ??
    getErrorMessage(updateHalPreOrder.error) ??
    getErrorMessage(cancelHalPreOrder.error) ??
    getErrorMessage(refreshHalTracking.error);
  const showPaymentLink =
    order?.paymentMethod === "online" && order?.status === "pending_payment";
  const retailWalkIn = order ? isRetailWalkInOrder(order) : false;

  const copyTracking = () => {
    const v = (trackingInput || order?.trackingNumber) ?? "";
    if (v) void navigator.clipboard.writeText(v);
  };

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "ຄຳສັ່ງຊື້", to: "/app/orders" },
              { label: id ?? "ລາຍລະອຽດ" },
            ]}
          />
        </Header>
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "ຄຳສັ່ງຊື້", to: "/app/orders" },
              { label: id ?? "ບໍ່ພົບ" },
            ]}
          />
        </Header>
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ບໍ່ພົບຄຳສັ່ງຊື້</p>
          </div>
          <Button variant="outline" onClick={() => nav({ to: "/app/orders" })}>
            ກັບຄືນລາຍການ
          </Button>
        </Main>
      </>
    );
  }

  const handleStatusSubmit = async (
    status: OrderStatus,
    note: string,
    attachments: { fileKey: string; blurhash?: string | null }[],
  ) => {
    const after = await updateStatus.mutateAsync({
      status,
      note: note || undefined,
    });
    if (attachments.length === 0) return;

    const kind =
      status === "packing"
        ? "packing"
        : status === "shipped"
          ? "shipping_document"
          : null;
    if (!kind) return;

    const existing = after.attachments.filter((a) => a.kind === kind);
    let sortBase =
      existing.length > 0
        ? Math.max(...existing.map((a) => a.sortOrder)) + 1
        : 0;

    for (const a of attachments) {
      await addAttachment.mutateAsync({
        kind,
        fileKey: a.fileKey,
        sortOrder: sortBase++,
        metadata: a.blurhash ? { blurhash: a.blurhash } : undefined,
      });
    }
    toast.success("ບັນທຶກຮູບຕາມສະຖານະສຳເລັດ");
  };

  const handleTrackingSubmit = () => {
    if (!trackingInput.trim()) return;
    updateTracking.mutate(
      { trackingNumber: trackingInput.trim() },
      {
        onSuccess: () => setTrackingInput(order.trackingNumber ?? ""),
      },
    );
  };

  //   const subtotal = order.items.reduce((sum, i) => sum + i.total, 0);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄຳສັ່ງຊື້", to: "/app/orders" },
            { label: order.code },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-bold text-2xl tracking-tight">
                ຄຳສັ່ງຊື້ #{order.code}
              </h1>
              <Badge variant="outline" className="font-normal">
                {ORDER_CHANNEL_LABELS[order.channel] ?? order.channel}
              </Badge>
              <Badge
                variant={STATUS_BADGE_VARIANTS[order.status] ?? "default"}
                className="font-medium"
              >
                {STATUS_BADGE_LO[order.status] ?? order.status}
              </Badge>
            </div>
            {order.channel === "social" ? (
              <p className="mt-2 text-muted-foreground text-sm">
                ແຫຼ່ງຂາຍ Social Media:{" "}
                <span className="font-medium text-foreground">
                  {order.socialSalesSource?.name ?? "ບໍ່ໄດ້ລະບຸ"}
                </span>
                {order.socialSalesSource?.code ? (
                  <span className="ml-2 opacity-80">
                    ({order.socialSalesSource.code})
                  </span>
                ) : null}
              </p>
            ) : null}
            <p className="mt-1 text-muted-foreground text-sm">
              ສັ່ງເມື່ອ {formatDateLocal(order.createdAt)} •{" "}
              {STATUS_LABELS[order.status] ?? order.status}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => nav({ to: "/app/orders" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            {nextStatuses.length > 0 && (
              <OrderStatusChangeForm
                orderId={order.id}
                nextStatuses={nextStatuses}
                onSubmit={handleStatusSubmit}
              />
            )}
            <PrintOrderButton order={order} />
          </div>
        </div>

        {order.status !== "cancelled" &&
          order.status !== "returned" &&
          order.status !== "return_in_transit" && (
            <Card className="mb-6">
              <CardContent>
                {retailWalkIn ? (
                  <RetailWalkInStatusBanner status={order.status} />
                ) : (
                  <OrderStatusStepper status={order.status} />
                )}
              </CardContent>
            </Card>
          )}

        <div className="grid gap-6 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-2">
            <OrderCustomerInfoCard
              order={order}
              orderId={order.id}
              retailWalkIn={retailWalkIn}
              footer={
                retailWalkIn ? null : (
                  <div>
                    <p className="text-muted-foreground uppercase tracking-wide">
                      ເລກຕິດຕາມ
                    </p>
                    <div className="mt-1 flex gap-1">
                      <Input
                        className="font-mono"
                        value={trackingInput}
                        onChange={(e) => setTrackingInput(e.target.value)}
                        placeholder="ກອກເລກຕິດຕາມ"
                        readOnly={!canEditTracking}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={copyTracking}
                        title="ຄັດລອກ"
                      >
                        <CopyIcon className="size-4" />
                      </Button>
                    </div>
                    {canEditTracking && (
                      <Button
                        size="sm"
                        className="mt-2"
                        disabled={
                          !trackingInput.trim() || updateTracking.isPending
                        }
                        onClick={handleTrackingSubmit}
                      >
                        ບັນທຶກ
                      </Button>
                    )}
                    {isHalOrder && (
                      <div className="mt-4 space-y-3 border-border border-t pt-4">
                        <div>
                          <p className="text-muted-foreground text-xs uppercase tracking-wide">
                            HAL Logistics
                          </p>
                          <div className="mt-1 space-y-1 text-sm">
                            <p>
                              Pre-order ID:{" "}
                              <span className="font-mono">
                                {order.halShipment?.preOrderId ?? "—"}
                              </span>
                            </p>
                            <p>
                              Shipment No:{" "}
                              <span className="font-mono">
                                {order.halShipment?.shipmentNumber ?? "—"}
                              </span>
                            </p>
                            <p>
                              Status ID:{" "}
                              <span className="font-mono">
                                {order.halShipment?.shipmentStatusId ?? "—"}
                              </span>
                            </p>
                            <p>
                              Delivery State ID:{" "}
                              <span className="font-mono">
                                {order.halShipment?.deliveryStateId ?? "—"}
                              </span>
                            </p>
                          </div>
                          {latestHalTrackingEvent ? (
                            <div className="mt-3 rounded-md border bg-muted/30 p-3 text-sm">
                              <p className="font-medium text-foreground">
                                {latestHalTrackingEvent.delivery_state_name ??
                                  "Latest HAL event"}
                              </p>
                              {latestHalTrackingEvent.message ? (
                                <p className="mt-1 text-muted-foreground">
                                  {latestHalTrackingEvent.message}
                                </p>
                              ) : null}
                              <p className="mt-1 text-muted-foreground text-xs">
                                {[
                                  latestHalTrackingEvent.place,
                                  latestHalTrackingEvent.date,
                                ]
                                  .filter(Boolean)
                                  .join(" • ") || "—"}
                              </p>
                            </div>
                          ) : null}
                          {halActionError ? (
                            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                              <p className="font-medium text-destructive">
                                HAL action failed
                              </p>
                              <p className="wrap-break-word mt-1 text-destructive/90">
                                {halActionError}
                              </p>
                            </div>
                          ) : null}
                        </div>
                        <PermissionGate
                          any={[Permissions.orders.update]}
                          fallback={null}
                        >
                          <div className="flex flex-wrap gap-2">
                            {!halPreOrderCreated ? (
                              <Button
                                type="button"
                                size="sm"
                                disabled={halActionPending}
                                onClick={() => createHalPreOrder.mutate()}
                              >
                                {createHalPreOrder.isPending
                                  ? "ກຳລັງສ້າງ..."
                                  : "Create HAL Shipment"}
                              </Button>
                            ) : (
                              <>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={
                                    halActionPending ||
                                    !canMutatePendingHalPreOrder
                                  }
                                  onClick={() => updateHalPreOrder.mutate()}
                                >
                                  {updateHalPreOrder.isPending
                                    ? "ກຳລັງອັບເດດ..."
                                    : "Update HAL"}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  disabled={
                                    halActionPending ||
                                    !canMutatePendingHalPreOrder
                                  }
                                  onClick={async () => {
                                    const ok = await confirm({
                                      title: "Cancel HAL pre-order",
                                      description:
                                        "Are you sure you want to cancel this HAL pre-order?",
                                      actionText: "Cancel HAL",
                                      ActionProps: { variant: "destructive" },
                                    });
                                    if (ok) cancelHalPreOrder.mutate();
                                  }}
                                >
                                  {cancelHalPreOrder.isPending
                                    ? "ກຳລັງຍົກເລີກ..."
                                    : "Cancel HAL"}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  disabled={
                                    halActionPending ||
                                    !order.halShipment?.shipmentNumber
                                  }
                                  onClick={() => refreshHalTracking.mutate()}
                                >
                                  {refreshHalTracking.isPending
                                    ? "ກຳລັງ refresh..."
                                    : "Refresh Tracking"}
                                </Button>
                              </>
                            )}
                          </div>
                          {halPreOrderCreated &&
                          !canMutatePendingHalPreOrder ? (
                            <p className="text-muted-foreground text-xs">
                              Update/Cancel ໄດ້ສະເພາະ HAL status pending (1)
                            </p>
                          ) : null}
                        </PermissionGate>
                      </div>
                    )}
                  </div>
                )
              }
            />

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <h2 className="font-semibold text-lg">ສະຖານະການຊຳລະ</h2>
                {/* <Badge
                  variant={order.status === "completed" ? "success" : "warning"}
                >
                  {order.status === "completed" ? "ຊຳລະແລ້ວ" : "ລໍຖ້າຊຳລະ"}
                </Badge> */}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm">
                  <p className="text-muted-foreground uppercase tracking-wide">
                    ຊ່ອງທາງຂາຍ
                  </p>
                  <p className="font-medium">
                    {ORDER_CHANNEL_LABELS[order.channel] ?? order.channel}
                  </p>
                  {order.channel === "social" && order.socialSalesSource ? (
                    <p className="mt-1 text-muted-foreground text-xs">
                      ແຫຼ່ງ: {order.socialSalesSource.name}
                      {order.socialSalesSource.code
                        ? ` (${order.socialSalesSource.code})`
                        : ""}
                    </p>
                  ) : null}
                </div>
                <div className="text-sm">
                  <p className="text-muted-foreground uppercase tracking-wide">
                    ວິທີຊຳລະ
                  </p>
                  <p className="font-medium">
                    {formatOrderPaymentMethodLabel(
                      order.paymentMethod,
                      order.channel,
                    )}
                  </p>
                </div>
                {canEditPendingOrderMeta && order.channel !== "retail" ? (
                  <PermissionGate
                    any={[Permissions.orders.update]}
                    fallback={null}
                  >
                    <div className="mt-2 space-y-6 border-border border-t pt-4">
                      <div className="space-y-2">
                        <p className="text-muted-foreground text-xs uppercase tracking-wide">
                          ແກ້ຊ່ອງທາງຂາຍ
                        </p>
                        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                          <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <Label className="text-muted-foreground text-xs">
                              ຊ່ອງທາງ
                            </Label>
                            <Select
                              value={channelDraft}
                              onValueChange={(v) => {
                                const c = v as OrderChannel;
                                setChannelDraft(c);
                                if (c !== "social") setSocialSourceDraft("");
                              }}
                            >
                              <SelectTrigger className="w-full max-w-[220px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="web">
                                  {ORDER_CHANNEL_LABELS.web}
                                </SelectItem>
                                <SelectItem value="retail">
                                  {ORDER_CHANNEL_LABELS.retail}
                                </SelectItem>
                                <SelectItem value="social">
                                  {ORDER_CHANNEL_LABELS.social}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {channelDraft === "social" ? (
                            <div className="flex min-w-0 flex-1 flex-col gap-1">
                              <Label className="text-muted-foreground text-xs">
                                ແຫຼ່ງ Social Media
                              </Label>
                              <Select
                                value={socialSourceDraft || "__none__"}
                                onValueChange={(v) =>
                                  setSocialSourceDraft(
                                    v === "__none__" ? "" : v,
                                  )
                                }
                                disabled={
                                  socialSourcesActiveQuery.isLoading ||
                                  socialSalesSourcesList.length === 0
                                }
                              >
                                <SelectTrigger className="w-full max-w-[260px]">
                                  <SelectValue placeholder="ເລືອກແຫຼ່ງຂາຍ" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">ບໍ່ລະບຸ</SelectItem>
                                  {socialSalesSourcesList.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                      {s.name}
                                      {s.code ? ` (${s.code})` : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : null}
                          <Button
                            type="button"
                            size="sm"
                            className="shrink-0 sm:self-end"
                            disabled={
                              updateChannel.isPending ||
                              !channelMetaDirty ||
                              (channelDraft === "social" &&
                                !socialSourceDraft.trim())
                            }
                            onClick={() => {
                              updateChannel.mutate({
                                channel: channelDraft,
                                socialSalesSourceId:
                                  channelDraft === "social"
                                    ? socialSourceDraft.trim()
                                    : null,
                              });
                            }}
                          >
                            {updateChannel.isPending ? "…" : "ບັນທຶກຊ່ອງທາງ"}
                          </Button>
                        </div>
                        <p className="text-muted-foreground text-xs leading-relaxed">
                          ອອກຈາກ Social ແລ້ວຖ້າມີຮູບສະລິບໂອນ ຕ້ອງລຶບສະລິບກ່ອນ.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-muted-foreground text-xs uppercase tracking-wide">
                          ແກ້ວິທີຊຳລະ (ກວດອີກຄັ້ງກ່ອນບັນທຶກ)
                        </p>
                        <div className="flex flex-wrap items-end gap-2">
                          <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <Label className="text-muted-foreground text-xs">
                              ເລືອກໃໝ່
                            </Label>
                            <Select
                              value={paymentMethodDraft}
                              onValueChange={(v) =>
                                setPaymentMethodDraft(v as "cod" | "online")
                              }
                            >
                              <SelectTrigger className="w-full max-w-[220px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cod">
                                  {ORDER_PAYMENT_METHOD_LABELS.cod}
                                </SelectItem>
                                <SelectItem value="online">
                                  {ORDER_PAYMENT_METHOD_LABELS.online}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            className="shrink-0"
                            disabled={
                              updatePaymentMethod.isPending ||
                              paymentMethodDraft === order.paymentMethod
                            }
                            onClick={() => {
                              if (paymentMethodDraft === order.paymentMethod)
                                return;
                              updatePaymentMethod.mutate({
                                paymentMethod: paymentMethodDraft,
                              });
                            }}
                          >
                            {updatePaymentMethod.isPending ? "…" : "ບັນທຶກວິທີຊຳລະ"}
                          </Button>
                        </div>
                        <p className="text-muted-foreground text-xs leading-relaxed">
                          ອະນຸຍາດສະເພາະອໍເດີ «ລໍຖ້າຊຳລະ» ທີ່ບໍ່ມີເງິນເກັບແລ້ວໃນລະບົບ.
                          ປ່ຽນເປັນປາຍທາງຈະຍົກເລີກລາຍການ payment pending.
                        </p>
                      </div>
                    </div>
                  </PermissionGate>
                ) : order.status === "pending_payment" &&
                  hasBlockingPaymentForMethodEdit ? (
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    ບໍ່ສາມາດແກ້ຊ່ອງທາງຂາຍ ຫຼື ວິທີຊຳລະອັດຕະໂນມັດ ເພາະມີລາຍການຊຳລະ«ຊຳລະແລ້ວ»
                    ຫຼື «ຄືນບາງສ່ວນ» ແລ້ວ — ກວດກັບຜູ້ດູແລລະບົບ.
                  </p>
                ) : null}
                {(order.payments ?? []).length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-muted-foreground text-xs">
                      ຄືນເງິນ (succeeded ລວມ): LAK{" "}
                      {formatPriceLak(order.refundTotalSucceeded)}
                    </p>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">
                      ລາຍການຊຳລະທີ່ກ່ຽວຂ້ອງ
                    </p>
                    <ul className="space-y-2">
                      {(order.payments ?? []).map((pay) => (
                        <li
                          key={pay.id}
                          className="rounded-md border bg-muted/30 px-3 py-2 text-sm"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span
                                className="truncate font-mono text-muted-foreground text-xs"
                                title={pay.id}
                              >
                                {pay.id.slice(0, 12)}…
                              </span>
                              <span>
                                LAK {formatPriceLak(pay.amount)}
                                {pay.paidAt && (
                                  <span className="ml-1 text-muted-foreground text-xs">
                                    • ຊຳລະ {formatDateLocal(pay.paidAt)}
                                  </span>
                                )}
                              </span>
                              {pay.externalId && (
                                <span
                                  className="truncate text-muted-foreground text-xs"
                                  title={pay.externalId}
                                >
                                  ID: {pay.externalId}
                                </span>
                              )}
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <Badge
                                variant={
                                  PAYMENT_STATUS_BADGE[pay.status] ?? "default"
                                }
                                className="font-medium"
                              >
                                {PAYMENT_STATUS_LABELS[pay.status] ??
                                  pay.status}
                              </Badge>
                              <a
                                href={`/app/payments/${pay.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="ເປີດລາຍລະອຽດການຊຳລະ"
                                className="inline-flex size-9 shrink-0 items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground"
                              >
                                <ExternalLinkIcon className="size-4" />
                              </a>
                            </div>
                          </div>
                          <PermissionGate
                            any={["payments:refund"]}
                            fallback={null}
                          >
                            <PaymentRefundBlock
                              orderId={order.id}
                              pay={pay}
                              orderStatus={order.status}
                            />
                          </PermissionGate>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {retailPaymentEmptyMessage(order)}
                  </p>
                )}
                {paymentSlipAttachments.length > 0 ? (
                  <div className="space-y-2 rounded-md border bg-muted/15 p-3">
                    <p className="font-medium text-foreground text-xs uppercase tracking-wide">
                      ສະລິບໂອນເງິນ (POS / ຊ່ອງSocial Media)
                    </p>
                    <OrderAttachmentGrid
                      items={paymentSlipAttachments}
                      canDelete={false}
                      onDelete={() => {}}
                      isDeletingId={null}
                      compact
                    />
                  </div>
                ) : null}
                {showPaymentLink && (
                  <>
                    <Button
                      className="w-full"
                      disabled
                      title="ມາພ້ອມ A5 Payment"
                    >
                      <LinkIcon className="size-4" />
                      ສ້າງລິ້ງຊຳລະ
                    </Button>
                    <p className="text-center text-muted-foreground text-xs">
                      ຊຳລະອອນໄລນ໌ຢ່າງປອດໄພ
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6 lg:col-span-3">
            {(order.status === "shipped" ||
              order.status === "completed" ||
              order.status === "return_in_transit" ||
              order.status === "returned") && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold text-lg">ຮັບຄືນ / return lines</h2>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  {order.status === "shipped" ||
                  order.status === "completed" ? (
                    <p className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-foreground text-sm">
                      ປ່ຽນເປັນ «ສິນຄ້າຕີກັບກຳລັງມາ» ກ່ອນ — ຈຶ່ງຈະບັນທຶກຮັບຄືນ ແລະ ໃຊ້ປຸ່ມ «ຕີກັບ /
                      ຮັບຄືນຄັງ» ໄດ້
                    </p>
                  ) : null}

                  {order.status === "returned" &&
                  order.returnLines.length > 0 ? (
                    <ul className="space-y-1 rounded-md border bg-muted/20 p-2 text-xs">
                      {order.returnLines.map((r) => {
                        const lineName = getOrderItemLabel(r.orderItemId);
                        return (
                          <li
                            key={r.id}
                            className="flex flex-wrap justify-between gap-2 border-border border-b pb-1 last:border-0"
                          >
                            <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
                              <span
                                className="truncate font-medium"
                                title={lineName}
                              >
                                {lineName}
                              </span>
                              <span className="shrink-0 text-muted-foreground">
                                ×{r.quantityReturned}
                              </span>
                            </span>
                            <span className="shrink-0 text-muted-foreground">
                              {r.stockAppliedAt ? "ຄືນສາງແລ້ວ" : "—"}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  {order.status === "returned" &&
                  order.returnLines.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      ປິດຮັບຄືນແລ້ວ (ບໍ່ມີບົດ return line — ຄືນສຕ໋ອກເຕັມອໍເດີ)
                    </p>
                  ) : null}

                  {order.status === "return_in_transit" ? (
                    <>
                      {order.returnLines.length > 0 ? (
                        <ul className="space-y-1 rounded-md border bg-muted/20 p-2 text-xs">
                          {order.returnLines.map((r) => {
                            const lineName = getOrderItemLabel(r.orderItemId);
                            return (
                              <li
                                key={r.id}
                                className="flex flex-wrap justify-between gap-2 border-border border-b pb-1 last:border-0"
                              >
                                <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
                                  <span
                                    className="truncate font-medium"
                                    title={lineName}
                                  >
                                    {lineName}
                                  </span>
                                  <span className="shrink-0 text-muted-foreground">
                                    ×{r.quantityReturned}
                                  </span>
                                </span>
                                <span className="shrink-0 text-muted-foreground">
                                  {r.stockAppliedAt
                                    ? "ຄືນສາງແລ້ວ"
                                    : "ລໍຖ້າປິດ returned"}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-muted-foreground text-sm">
                          ຍັງບໍ່ມີລາຍການຮັບຄືນ (ສາມາດວ່າງແລ້ວຄືນເຕັມໃນເມື່ອປິດ returned
                          ໂດຍບໍ່ມີບົດ)
                        </p>
                      )}
                      <div className="space-y-2 rounded-md border border-dashed p-3">
                        <p className="font-medium text-sm">
                          ເພີ່ມບົດລະຮັບຄືນ (ກ່ອນປິດສະຖານະ returned)
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          <div>
                            <Label className="text-xs">ລາຍການ</Label>
                            {returnLineItemOptions.length === 0 ? (
                              <p className="text-muted-foreground text-sm">
                                ບໍ່ມີຈຳນວນຄືນເຫຼືອ (ລາຍການນີ້ຄືນຄົບແລ້ວ)
                              </p>
                            ) : (
                              <Select
                                value={resolvedReturnItemId}
                                onValueChange={setReturnItemId}
                              >
                                <SelectTrigger className="h-9 w-full">
                                  <SelectValue placeholder="ເລືອກ" />
                                </SelectTrigger>
                                <SelectContent>
                                  {returnLineItemOptions.map((it) => {
                                    const can = remainingReturnable(it);
                                    return (
                                      <SelectItem key={it.id} value={it.id}>
                                        {(it.productName ?? it.id).slice(0, 40)}{" "}
                                        ({it.quantity} ສັ່ງ, ເຫຼືອຄືນ {can} ຊິ້ນ)
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                          <div>
                            <Label className="text-xs">ຈຳນວນຄືນ</Label>
                            <Input
                              type="number"
                              min={1}
                              max={maxReturnQty}
                              value={returnQty}
                              onChange={(e) => {
                                const raw = Math.floor(Number(e.target.value));
                                if (Number.isNaN(raw) || raw < 1) {
                                  setReturnQty(1);
                                  return;
                                }
                                setReturnQty(Math.min(raw, maxReturnQty));
                              }}
                            />
                            {selectedReturnItem ? (
                              <p className="text-muted-foreground text-xs">
                                ສູງສຸດ {maxReturnQty} ຊິ້ນ (ສັ່ງ{" "}
                                {selectedReturnItem.quantity} ຊິ້ນ, ມີ return ແລ້ວ{" "}
                                {(selectedReturnItem.quantityReturnStocked ??
                                  0) +
                                  (selectedReturnItem.quantityReturnPending ??
                                    0)}{" "}
                                ຊິ້ນ)
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <Textarea
                          value={returnReason}
                          onChange={(e) => setReturnReason(e.target.value)}
                          placeholder="ເຫດຜົນ (ທາງເລືອກ)"
                          rows={2}
                        />
                        <Button
                          type="button"
                          size="sm"
                          disabled={
                            addReturnLines.isPending ||
                            !resolvedReturnItemId ||
                            returnLineItemOptions.length === 0
                          }
                          onClick={() => {
                            void addReturnLines.mutateAsync({
                              lines: [
                                {
                                  orderItemId: resolvedReturnItemId,
                                  quantityReturned: returnQty,
                                  reason: returnReason || undefined,
                                },
                              ],
                            });
                            setReturnReason("");
                          }}
                        >
                          {addReturnLines.isPending ? "…" : "ບັນທຶກຮັບຄືນ"}
                        </Button>
                      </div>
                    </>
                  ) : null}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <h2 className="font-semibold text-lg">ລາຍການສິນຄ້າ</h2>
                {canEditOrderItems ? (
                  <PermissionGate
                    any={[Permissions.orders.update]}
                    fallback={null}
                  >
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setEditItemsOpen(true)}
                    >
                      <PencilIcon className="mr-1.5 size-3.5" />
                      ແກ້ລາຍການສິນຄ້າ
                    </Button>
                  </PermissionGate>
                ) : null}
              </CardHeader>
              <CardContent>
                <OrderItemsTable items={order.items} />
                <div className="mt-6 flex flex-col items-end gap-1 text-sm">
                  <div className="flex w-full max-w-xs justify-between">
                    <span className="text-muted-foreground">ຍອດຍ່ອຍ</span>
                    <span>
                      LAK{" "}
                      {formatPriceLak(
                        order.items.reduce((s, i) => s + i.total, 0),
                      )}
                    </span>
                  </div>
                  <div className="flex w-full max-w-xs justify-between">
                    <span className="text-muted-foreground">
                      ຄ່າສົ່ງ
                      {order.shipping?.shippingZone
                        ? ` (${order.shipping.shippingZone})`
                        : ""}
                    </span>
                    <span>
                      {order.shippingFee != null
                        ? `LAK ${formatPriceLak(order.shippingFee)}`
                        : "—"}
                    </span>
                  </div>
                  {order.discountTotal > 0 ? (
                    <div className="flex w-full max-w-xs justify-between">
                      <span className="text-muted-foreground">ສ່ວນຫຼຸດ</span>
                      <span>− LAK {formatPriceLak(order.discountTotal)}</span>
                    </div>
                  ) : null}
                  {order.status === "pending_payment" ? (
                    <div className="flex w-full max-w-xs flex-col gap-2 border-border border-t border-dashed pt-3">
                      <Label className="text-muted-foreground text-xs">
                        ປັບສ່ວນຫຼຸດ (ສູງສຸດ LAK {formatPriceLak(maxDiscountAllowed)})
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          className="font-mono"
                          inputMode="numeric"
                          value={discountInput}
                          onChange={(e) => setDiscountInput(e.target.value)}
                          disabled={updateDiscount.isPending}
                        />
                        <Button
                          type="button"
                          size="sm"
                          disabled={updateDiscount.isPending}
                          onClick={() => {
                            const digits = discountInput.replace(/\D/g, "");
                            const n = digits ? Number.parseInt(digits, 10) : 0;
                            if (!Number.isFinite(n) || n < 0) {
                              toast.error("ສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ");
                              return;
                            }
                            void updateDiscount.mutateAsync({
                              discountTotal: n,
                            });
                          }}
                        >
                          {updateDiscount.isPending ? "…" : "ບັນທຶກ"}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-2 flex w-full max-w-xs justify-between pt-2 font-semibold text-base">
                    <span>ຍອດລວມທີ່ຕ້ອງຊຳລະ</span>
                    <span>LAK {formatPriceLak(order.total)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="font-semibold text-lg">ປະຫວັດສະຖານະຄຳສັ່ງຊື້</h2>
              </CardHeader>
              <CardContent>
                <OrderStatusHistoryTimeline order={order} />
              </CardContent>
            </Card>
          </div>
        </div>
      </Main>
      {order && canEditOrderItems ? (
        <OrderEditItemsDialog
          open={editItemsOpen}
          onOpenChange={setEditItemsOpen}
          order={order}
        />
      ) : null}
    </>
  );
}

function OrderItemsTable({ items }: { items: OrderDetailDTO["items"] }) {
  if (!items.length) {
    return <p className="text-muted-foreground text-sm">ບໍ່ມີລາຍການ</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="pb-2 font-medium">ສິນຄ້າ ແລະ ຕົວແປ</th>
            <th className="pb-2 text-right font-medium">ຈຳນວນ</th>
            <th className="pb-2 text-right font-medium">ຄືນສາງ</th>
            <th className="pb-2 text-right font-medium">ລໍຖ້າ</th>
            <th className="pb-2 text-right font-medium">ລາຄາຕໍ່ຫນ່ວຍ</th>
            <th className="pb-2 text-right font-medium">ຍອດຍ່ອຍ</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} className="border-b">
              <td className="py-3">
                <div className="flex items-center gap-3">
                  <div className="size-12 shrink-0 overflow-hidden rounded-md border bg-muted">
                    <AppImage
                      src={row.imageFileKey ?? undefined}
                      alt={row.productName ?? ""}
                      className="size-full"
                      showLoading={false}
                    />
                  </div>
                  <div>
                    <p className="font-medium">{row.productName ?? "—"}</p>
                    <p className="text-muted-foreground text-xs">
                      {formatPrintVariantOptions(row.resolvedVariantOptions) ??
                        row.variantSku ??
                        "—"}
                    </p>
                  </div>
                </div>
              </td>
              <td className="text-right">{row.quantity}</td>
              <td className="text-right text-muted-foreground">
                {row.quantityReturnStocked ?? 0}
              </td>
              <td className="text-right text-muted-foreground">
                {row.quantityReturnPending ?? 0}
              </td>
              <td className="text-right">LAK {formatPriceLak(row.price)}</td>
              <td className="text-right font-medium">
                LAK {formatPriceLak(row.total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
